import { Dispatch, SetStateAction, useEffect } from 'react';
import { RichText } from 'prismic-reactjs';
import CharacterSad from '@monofront/design-system/visuals/Illustrations/CharacterSad';
import Divider from '@monofront/design-system/components/Divider';
import InformativeBox from '@monofront/design-system/components/InformativeBox';
import InputWithButton from '@monofront/design-system/components/Form/InputWithButton';
import Label from '@monofront/design-system/components/Form/Label';
import { useShoppingCart, useSideCart } from '@hooks/cart/useShoppingCart';
import { useTranslations } from 'next-intl';
import { PrismicRichTextInjecter } from '@monofront/slice-machine/helpers';
import { toast } from 'react-toastify';
import logger from '@utils/logger';
import {
  SUBSCRIBE_TO_FRESH_WAITLIST,
  SUBSCRIBE_TO_ULTRA_FRESH_WAITLIST,
} from '@monofront/business/queries';
import { useMutation } from '@apollo/client';
import { ELIGIBILITY_STATUS, EMAIL_REGEX } from '@monofront/business/constants';
import { useRouter } from 'next/router';
import { useEligibility } from '@monofront/business/contexts';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import { IPrismicProductEligibilityModal } from './types';
import { useGrowthBook } from '@growthbook/growthbook-react';

interface IFreshAddressNotEligibleProps {
  setFreshStatus: Dispatch<SetStateAction<ELIGIBILITY_STATUS>>;
  hasNotificationForm?: boolean;
  hasRegisteredAddress: boolean;
  document?: IPrismicProductEligibilityModal;
}

const FreshAddressNotEligible: React.FC<IFreshAddressNotEligibleProps> = ({
  setFreshStatus,
  hasNotificationForm = false,
  document,
}) => {
  const { address, eligibility } = useEligibility();
  const {
    shoppingCartId,
    shoppingCart,
    deleteFreshAndUltraFreshProductsFromCart: deleteFreshProductsFromCart,
  } = useShoppingCart();
  const router = useRouter();
  const { dispatchSideCart } = useSideCart();
  const [addToFreshWaitList, { loading: isFreshSubscriptionLoading }] =
    useMutation(SUBSCRIBE_TO_FRESH_WAITLIST);
  const [
    addToUltraFreshWaitList,
    { loading: isUltraFreshSubscriptionLoading },
  ] = useMutation(SUBSCRIBE_TO_ULTRA_FRESH_WAITLIST);
  const { hasFreshProduct, hasUltraFreshProduct } = shoppingCart;
  const tGlobal = useTranslations('Global');
  const tFresh = useTranslations('Fresh');
  const tAccount = useTranslations('Account');

  const loading = isFreshSubscriptionLoading || isUltraFreshSubscriptionLoading;

  // Debugging hooks
  const growthbook = useGrowthBook();

  useEffect(() => {
    const postalCode = address?.postalCode || '';

    if (
      postalCode.startsWith('75') ||
      postalCode.startsWith('77') ||
      postalCode.startsWith('78') ||
      postalCode.startsWith('91') ||
      postalCode.startsWith('92') ||
      postalCode.startsWith('93') ||
      postalCode.startsWith('94') ||
      postalCode.startsWith('95')
    ) {
      logger.error(
        new Error(
          'We are showing the FreshAddressNonEligible modal to an eligible user.',
        ),
        {
          eligibility,
          address,
          growthbookStatus: growthbook?.ready,
        },
      );
    }
  }, [address?.postalCode]);

  const onSubmit = async ({ email }: { [key: string]: string }) => {
    try {
      const variables = {
        email,
        postalCode: address?.postalCode,
        street: address?.street,
        city: address?.city,
        country: address?.country,
      };

      await Promise.all([
        addToFreshWaitList({ variables }),
        addToUltraFreshWaitList({ variables }),
      ]);

      toast(tFresh('notificationRequestSuccess'));
      dispatchSideCart({ type: 'close' });
    } catch (e: any) {
      toast(tGlobal('errorOccurredTryAgain'), {
        progressClassName: 'toast-error',
      });
      logger.error('Error during fresh list subscription', { error: e });
    }
  };

  const onClickRemoveProducts = async () => {
    try {
      if (!shoppingCartId) {
        throw new Error('Unexpected empty `shoppingCartId`');
      }

      dispatchSideCart({ type: 'viewMyCart' });
      await deleteFreshProductsFromCart(shoppingCartId, shoppingCart.products);
    } catch (e) {
      logger.error('Error when removing fresh products', e);
      toast(tGlobal('errorOccurredTryAgain'), {
        progressClassName: 'toast-error',
      });
    }
  };

  if (!document) return <HeartLoader />;

  return (
    <>
      <InformativeBox
        hasPadding={false}
        hasImageOnMobile
        backgroundColor="transparent"
        icon={<CharacterSad aria-hidden="true" />}
      >
        <RichText
          render={document.not_eligible_informative_box_text}
          htmlSerializer={PrismicRichTextInjecter({
            '%ADDRESS%': `${address?.postalCode}, ${address?.city}`,
          })}
        />
      </InformativeBox>

      {hasNotificationForm && !(hasFreshProduct || hasUltraFreshProduct) && (
        <>
          <Divider marginVertical="tw-my-4" />
          <p>{tFresh('emailInputHelper')}</p>
          <div className="form">
            <Label id="email">{tFresh('emailInputLabel')}</Label>
            <InputWithButton
              handleSubmit={onSubmit}
              inputProps={{
                id: 'email',
                name: 'email',
                type: 'email',
              }}
              registerOptions={{
                required: {
                  value: true,
                  message: tAccount('useValidEmail'),
                },
                pattern: {
                  value: EMAIL_REGEX,
                  message: tAccount('useValidEmail'),
                },
              }}
              buttonProps={{ variant: 'primary', isLoading: loading }}
            >
              {tGlobal('send')}
            </InputWithButton>
          </div>
          <Divider marginVertical="tw-my-4" />
          <RichText render={document.not_eligible_extra_infos} />
        </>
      )}

      <div className="tw-fixed tw-bottom-0 tw-left-0 !tw-m-0 tw-h-[140px] tw-w-full tw-bg-red-100 tw-px-3 tw-py-4">
        <button
          data-testid="FreshAddressNotEligible-continue-button"
          onClick={() => {
            if (hasFreshProduct || hasUltraFreshProduct) {
              onClickRemoveProducts();
            } else {
              dispatchSideCart({ type: 'close' });
              router.push('/');
            }
          }}
          className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-my-2 tw-w-full"
        >
          {hasFreshProduct || hasUltraFreshProduct
            ? document.delete_uneligible_products_cta_label
            : document.close_modal_cta_label}
        </button>
        <button
          className="tw-lf-button tw-lf-button-secondary tw-lf-button-md tw-my-2 tw-w-full tw-bg-white"
          onClick={() => setFreshStatus(ELIGIBILITY_STATUS.ADDRESS_FORM)}
        >
          {document.start_again_cta_label}
        </button>
      </div>
      <style jsx>{`
        .form {
          margin-top: var(--space-4);
        }

        .form > :global(label) {
          margin-bottom: var(--space-1);
          font-weight: var(--font-weight-base);
        }
      `}</style>
    </>
  );
};

export default FreshAddressNotEligible;
